import React, {useCallback, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {debtDate, debtMoney} from '../../features/payment/BalanceDeadlineNotice';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const defaults = {enabled: false, threshold: 50, grace_days: 7, extension_days: 7};
const errorMessage = failure => typeof failure.response?.data?.detail === 'string'
  ? failure.response.data.detail : 'Die Änderung konnte nicht gespeichert werden. Bitte erneut versuchen.';

export default function BalanceDeadlineSettings({departmentId}) {
  const [policy, setPolicy] = useState(defaults);
  const [employees, setEmployees] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [extension, setExtension] = useState(null);
  const working = useRef(false);
  const dialog = useRef(null);
  const extensionOpen = Boolean(extension);
  useEffect(() => {
    if (!extensionOpen) return undefined;
    const previous = document.activeElement;
    dialog.current?.querySelector('input')?.focus();
    return () => previous?.focus();
  }, [extensionOpen]);
  const generation = useRef(0);
  const endpoint = `${API}/department-admin/${departmentId}`;
  const load = useCallback(async () => {
    const current = generation.current;
    try {
      const response = await axios.get(`${API}/department-admin/${departmentId}/debt-policy`);
      if (current !== generation.current) return;
      setPolicy(response.data.policy); setEmployees(response.data.employees); setLoaded(true); setError('');
    } catch (_) {if (current === generation.current) setError('Zahlungsfristen konnten nicht geladen werden.');}
  }, [departmentId]);
  useEffect(() => {generation.current += 1; setLoaded(false); load(); return () => {generation.current += 1;};}, [load]);
  const save = async event => {
    event.preventDefault();
    if (working.current) return;
    working.current = true; setBusy(true); setError(''); setMessage('');
    try {
      const response = await axios.put(`${endpoint}/debt-policy`, {...policy,
        threshold: Number(policy.threshold), grace_days: Number(policy.grace_days), extension_days: Number(policy.extension_days)});
      setPolicy(response.data.policy); setEmployees(response.data.employees); setMessage('Zahlungsfristen gespeichert.');
    } catch (failure) {setError(errorMessage(failure));}
    finally {working.current = false; setBusy(false);}
  };
  const grant = async event => {
    event.preventDefault();
    if (working.current) return;
    working.current = true; setBusy(true); setError(''); setMessage('');
    try {
      await axios.post(`${endpoint}/employees/${extension.employee_id}/debt-extension`,
        {days: Number(extension.days), reason: extension.reason});
      setExtension(null); setMessage('Nachfrist gewährt. Bestellungen sind wieder möglich.');
      await load();
    } catch (failure) {setError(errorMessage(failure));}
    finally {working.current = false; setBusy(false);}
  };
  return <section>
    <h2 className="text-2xl font-bold">Saldo &amp; Zahlungsfristen</h2>
    <p className="mt-2 text-sm text-gray-600">Bei getrennten Konten wird jedes Konto einzeln geprüft. Guthaben und Schulden werden nicht zwischen den Kassen verrechnet. Erreicht mindestens ein Konto die Grenze, beginnt die gemeinsame Zahlungsfrist. Angezeigt wird der höchste offene Kontobetrag.</p>
    {error && <p role="alert" className="mt-4 rounded-lg bg-red-50 p-3 text-red-800">{error} {!loaded && <button onClick={load} className="underline">Erneut laden</button>}</p>}
    {message && <p role="status" className="mt-4 rounded-lg bg-green-50 p-3 text-green-900">{message}</p>}
    {!loaded ? <p className="mt-4">Einstellungen werden geladen …</p> : <>
      <form onSubmit={save} className="mt-6 space-y-4">
        <label className="flex items-center gap-3 font-medium"><input type="checkbox" checked={policy.enabled} disabled={busy} onChange={e => setPolicy({...policy, enabled: e.target.checked})} />Zahlungsfristen aktivieren</label>
        <div className="grid gap-4 sm:grid-cols-3">
          {[
            ['threshold', 'Prüfgrenze (€)', '0.01', 0.01, 100000],
            ['grace_days', 'Zahlungsfrist (Tage)', '1', 1, 365],
            ['extension_days', 'Standard-Nachfrist (Tage)', '1', 1, 365],
          ].map(([key, label, step, min, max]) => <label key={key} className="text-sm font-medium">{label}<input type="number" required min={min} max={max} step={step} value={policy[key]} disabled={busy}
            onChange={e => setPolicy({...policy, [key]: e.target.value})} className="mt-1 block w-full rounded-lg border p-3" /></label>)}
        </div>
        <p className="text-sm text-gray-600">Nach Fristablauf werden alle Bestellungen gesperrt. Aufforderung und Sperre entfallen, sobald die Schulden auf jedem Konto unter der Grenze liegen. Beim gemeinsamen Konto zählt dessen Saldo.</p>
        <p className="text-sm text-gray-600">Bestehende Schulden erhalten beim Aktivieren die volle Frist. Geänderte Fristdauern gelten für neue Fälle. Deaktivieren beendet laufende Zahlungsaufforderungen. Ein Tag entspricht 24 Stunden.</p>
        <button disabled={busy} className="rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{busy ? 'Wird gespeichert …' : 'Einstellungen speichern'}</button>
      </form>
      <div className="mt-8 flex items-center justify-between gap-3"><h3 className="text-lg font-semibold">Offene Zahlungsaufforderungen</h3><button disabled={busy} onClick={load} className="text-sm underline">Aktualisieren</button></div>
      {employees.length === 0 ? <p className="mt-3 text-sm text-gray-600">Keine offenen Zahlungsaufforderungen.</p> : <div className="mt-3 divide-y rounded-lg border">
        {employees.map(employee => <div key={employee.employee_id} className="flex flex-wrap items-center justify-between gap-3 p-4">
          <div><p className="font-semibold">{employee.name} · {debtMoney(employee.debt)}</p><p className="text-sm">{employee.blocked ? 'Bestellungen gesperrt' : 'Zahlungsfrist läuft'} · bis {debtDate(employee.due_at)} Uhr</p>
            {employee.extensions.length > 0 && <details className="mt-1 text-xs text-gray-600"><summary>Nachfristen anzeigen ({employee.extensions.length})</summary>
              {employee.extensions.map((entry, index) => <p key={index} className="mt-1">{debtDate(entry.at)} Uhr · {entry.days} Tage · {entry.actor}{entry.reason ? ` · ${entry.reason}` : ''}</p>)}
            </details>}
          </div>
          <button disabled={busy} onClick={() => setExtension({...employee, days: policy.extension_days, reason: ''})} className="rounded-lg border px-3 py-2">Nachfrist gewähren</button>
        </div>)}
      </div>}
    </>}
    {extension && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <form ref={dialog} onSubmit={grant} role="dialog" aria-modal="true" aria-labelledby="extension-title"
        onKeyDown={event => {
          if (event.key === 'Escape' && !busy) {event.stopPropagation(); setExtension(null);}
          if (event.key !== 'Tab') return;
          const controls = [...dialog.current.querySelectorAll('input:not(:disabled),button:not(:disabled)')];
          const first = controls[0], last = controls[controls.length - 1];
          if (event.shiftKey && document.activeElement === first) {event.preventDefault(); last?.focus();}
          if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first?.focus();}
        }} className="w-full max-w-md space-y-4 rounded-xl bg-white p-6">
        <h3 id="extension-title" className="text-xl font-semibold">Nachfrist für {extension.name}</h3>
        <p className="text-sm">Die Nachfrist verlängert die bestehende Frist. Ist diese abgelaufen, beginnt die Nachfrist jetzt.</p>
        <label className="block text-sm">Zusätzliche Tage<input type="number" required min="1" max="365" step="1" disabled={busy} value={extension.days} onChange={e => setExtension({...extension, days: e.target.value})} className="mt-1 block w-full rounded-lg border p-3" /></label>
        <label className="block text-sm">Notiz (optional)<input maxLength="300" value={extension.reason} disabled={busy} onChange={e => setExtension({...extension, reason: e.target.value})} className="mt-1 block w-full rounded-lg border p-3" /></label>
        {error && <p role="alert" className="text-sm text-red-800">{error}</p>}
        <div className="flex gap-3"><button type="button" disabled={busy} onClick={() => setExtension(null)} className="rounded-lg border px-4 py-2">Abbrechen</button><button disabled={busy} className="rounded-lg bg-blue-700 px-4 py-2 text-white">Nachfrist bestätigen</button></div>
      </form>
    </div>}
  </section>;
}
